import React, { useMemo } from 'react';
import {
    Button,
    ButtonGroup,
    ButtonProps,
    Divider,
    Flex,
    Heading,
    useTheme
} from '@chakra-ui/react';
import { CurrencyInput } from 'ts/common/components';
import { useTranslate } from 'ts/common/hooks';

type Props = {
    value: number;
    invoice: SpApi.Client.IInvoice;
    totalPaymentAmount: number;
    onChange: (value: number) => void;
};

const getButtonProps = (isSelected: boolean): ButtonProps => {
    return {
        variant: 'outline primary',
        borderWidth: '2px',
        borderRadius: '8px',
        flex: 1,
        ...(isSelected && {
            backgroundColor: 'brand.primary',
            color: 'white',
            _focus: {
                backgroundColor: 'brand.primary',
                color: 'white'
            }
        })
    };
};

type TipOption = null | 'none' | 'option1' | 'option2' | 'option3';

function InvoiceTipSection({ value, invoice, totalPaymentAmount, onChange }: Props) {
    const t = useTranslate('clientInvoice');
    const theme = useTheme();

    const { currencyCode, tipSuggestion1, tipSuggestion2, tipSuggestion3 } = invoice;

    const option1 = tipSuggestion1 ?? 15;
    const option2 = tipSuggestion2 ?? 20;
    const option3 = tipSuggestion3 ?? 25;

    const option1Dollars = useMemo(() => {
        return (Number(totalPaymentAmount) * option1) / 100;
    }, [totalPaymentAmount, option1]);

    const option2Dollars = useMemo(() => {
        return (Number(totalPaymentAmount) * option2) / 100;
    }, [totalPaymentAmount, option2]);

    const option3Dollars = useMemo(() => {
        return (Number(totalPaymentAmount) * option3) / 100;
    }, [totalPaymentAmount, option3]);

    const handleButtonClick = (option: TipOption) => {
        let tipAmount = 0;

        if (option === 'option1') {
            tipAmount = option1Dollars;
        } else if (option === 'option2') {
            tipAmount = option2Dollars;
        } else if (option === 'option3') {
            tipAmount = option3Dollars;
        }

        onChange(tipAmount);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = Number(event.target.value);

        onChange(numericValue);
    };

    const selectedOption: TipOption = useMemo(() => {
        if (value === 0) {
            return 'none';
        } else if (value === option1Dollars) {
            return 'option1';
        } else if (value === option2Dollars) {
            return 'option2';
        } else if (value === option3Dollars) {
            return 'option3';
        }

        return null;
    }, [value, option1Dollars, option2Dollars, option3Dollars]);

    return (
        <>
            <Flex flexDirection="column" gap="16px" fontFamily={theme.fonts.body}>
                <Heading as="h3" size="lg" margin={0}>
                    {t('tipHeading')}
                </Heading>

                <Flex gap="16px" direction="column">
                    <ButtonGroup gap="8px">
                        <Button
                            {...getButtonProps(selectedOption === 'none')}
                            isSelected={selectedOption === 'none'}
                            onClick={() => handleButtonClick('none')}
                        >
                            {t('noTip')}
                        </Button>
                        <Button
                            {...getButtonProps(selectedOption === 'option1')}
                            isSelected={selectedOption === 'option1'}
                            onClick={() => handleButtonClick('option1')}
                        >
                            {option1}%
                        </Button>

                        <Button
                            {...getButtonProps(selectedOption === 'option2')}
                            isSelected={selectedOption === 'option2'}
                            onClick={() => handleButtonClick('option2')}
                        >
                            {option2}%
                        </Button>

                        <Button
                            {...getButtonProps(selectedOption === 'option3')}
                            isSelected={selectedOption === 'option3'}
                            onClick={() => handleButtonClick('option3')}
                        >
                            {option3}%
                        </Button>
                    </ButtonGroup>
                    <CurrencyInput
                        currencyCode={currencyCode}
                        value={value}
                        onChange={handleInputChange}
                    />
                </Flex>
            </Flex>
            <Divider />
        </>
    );
}

export default InvoiceTipSection;
